import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, TextField, Typography } from '@mui/material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import JobService from '../../services/JobService';
import { errorMessage } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { fmtBytes, todayIso } from '../../utils/format';
import { ACCEPT_ANY, ACCEPT_IMAGE, isAcceptedFile, useFilePicker, useUploader } from './files';

/**
 * Confirms that the work on site is done (ticks the category's field step), with an optional note (saved to the
 * job's notes) and optional photos/files (saved to the job's documents). Undo in the toast unticks the step only.
 */
export default function FieldDoneDialog({ job, step, onClose, onDone }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [date, setDate] = useState('');
    const [note, setNote] = useState('');
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const { upload, progress } = useUploader(job?.id);

    useEffect(() => {
        if (!job) return;
        // The planned day if it has come, otherwise today.
        const today = todayIso();
        setDate(job.scheduledDate && job.scheduledDate <= today ? job.scheduledDate : today);
        setNote('');
        setFiles([]);
    }, [job]);

    const addFiles = (list) => {
        const picked = Array.from(list || []);
        if (picked.some(f => !isAcceptedFile(f))) showToast(t('files.unsupported'), 'error');
        setFiles(fs => [...fs, ...picked.filter(isAcceptedFile)]);
    };
    const [fileInput, pickFiles] = useFilePicker(addFiles, { accept: ACCEPT_ANY });
    const [cameraInput, takePhoto] = useFilePicker(addFiles, { accept: ACCEPT_IMAGE, capture: true });

    const confirm = async () => {
        if (!date) return;
        setBusy(true);
        try {
            await JobService.setStep(job.id, step, true, date);
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
            setBusy(false);
            return;
        }
        // The field work is recorded; a failing note or upload shows its own error but does not undo it.
        try {
            if (note.trim()) await JobService.addNote(job.id, note);
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
        }
        if (files.length) await upload(files);
        setBusy(false);
        showToast(t('planner.fieldDoneToast'), 'success', {
            action: { label: t('workflow.undo'), onClick: () => JobService.setStep(job.id, step, false).then(onDone).catch(() => {}) },
        });
        onDone();
    };

    return (
        <Dialog open={!!job} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 18, pb: 0.5 }}>{t('fieldDone.title')}</DialogTitle>
            {job && (
                <DialogContent>
                    <Typography sx={{ fontSize: 13.5, color: '#64748B', mb: 2.5 }}>
                        {[job.number, job.title, job.client?.name].filter(Boolean).join(' · ')}
                    </Typography>
                    <TextField size="small" type="date" label={t(`steps.${step}`)} value={date} onChange={(e) => setDate(e.target.value)}
                               slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: todayIso() } }} sx={{ mb: 2.5, minWidth: 220 }} />
                    <TextField fullWidth multiline minRows={3} size="small" label={t('fieldDone.note')} placeholder={t('fieldDone.notePlaceholder')}
                               value={note} onChange={(e) => setNote(e.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 10000 } }} />
                    <Typography sx={{ fontSize: 12, color: '#94A3B8', mt: 0.5, mb: 2 }}>{t('fieldDone.noteHint')}</Typography>

                    {fileInput}{cameraInput}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button size="small" variant="outlined" startIcon={<PhotoCameraOutlinedIcon />} onClick={takePhoto} disabled={busy}>{t('files.takePhoto')}</Button>
                        <Button size="small" variant="outlined" startIcon={<UploadFileOutlinedIcon />} onClick={pickFiles} disabled={busy}>{t('files.addFile')}</Button>
                    </Box>
                    {files.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.5 }}>
                            {files.map((f, i) => (
                                <Chip key={`${f.name}-${i}`} size="small" label={`${f.name} · ${fmtBytes(f.size)}`} disabled={busy}
                                      onDelete={() => setFiles(fs => fs.filter((_, k) => k !== i))} sx={{ maxWidth: '100%' }} />
                            ))}
                        </Box>
                    )}
                    {progress !== null && <LinearProgress variant="determinate" value={progress} sx={{ mt: 2 }} />}
                </DialogContent>
            )}
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={busy} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                <Button variant="contained" color="success" disableElevation startIcon={busy ? null : <CheckCircleOutlineIcon />}
                        onClick={confirm} disabled={busy || !date} sx={{ minWidth: 140 }}>
                    {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('fieldDone.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
